import { DestroyRef, inject, Injectable } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { Store } from "@ngxs/store";
import { ProfileModel } from "@shared/interfaces/profile.model";
import { EMPTY } from "rxjs";
import { catchError, map } from "rxjs/operators";
import {
  SET_IS_PASSWORD_EXPIRED_IND,
  UPDATE_PREFER_NAME,
} from "src/app/store/user/user.action";

import {
  GET_CPD_STATUS,
  GET_ELEARNING_LOV,
  GET_FILTERED_AGENT_LIST,
  PROFILE_CHANGE_SECRET_ANSWER,
  PROFILE_UPDATE_CONTACT_DETAILS,
  RESET_PROFILE_STATE_RESPONSE,
  RESET_SEARCH_ELEARNING_DATA,
  SEARCH_ELEARNING,
} from "./profile.action";
import { CHANGE_PASSWORD, GET_PROFILE_DETAILS } from "./profile.action";
import { ProfileService } from "./profile.service";

@State<ProfileModel>({
  name: "ProfileState",
  defaults: {
    profile: null,
    eLearninglOV: null,
    searchedELearning: null,
    cpdDownloadUrl: null,
    changePasswordState: {
      isChangePasswordSuccess: false,
      changePasswordErrorMsg: null,
      changePasswordErrorTitle: null,
    },
    updateContactDetails: {
      isUpdateContactDetailsSuccess: false,
      updateContactDetailsErrorTitle: null,
      updateContactDetailsErrorMsg: null,
    },
    updateSecretQuestionState: {
      isUpdateSecretQuestionSuccess: false,
      updateSecretQuestionErrorTitle: null,
      updateSecretQuestionErrorMsg: null,
    },
    downloadCpdPdf: null,
    downloadCpdPdfUrl: null,
    showCPD: false,
    branchList: null,
    totalCpdRequiredRes: 0,
    totalCpdHoursRes: 0,
    totalShortFallRes: 0,
  },
})
@Injectable()
export class ProfileState {
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly profileService: ProfileService,
    private readonly store: Store,
  ) {}

  private normalizeAgentList(
    agentList: ProfileModel["eLearninglOV"]["agentList"],
  ) {
    return (agentList ?? []).map((agent) => ({
      ...agent,
      code: agent.code ?? agent.agentCode,
    }));
  }

  @Selector()
  public static searchedELearning(state: ProfileModel) {
    return state.searchedELearning;
  }

  @Selector()
  public static totalCpdRequiredRes(state: ProfileModel) {
    return state.totalCpdRequiredRes;
  }

  @Selector()
  public static totalCpdHoursRes(state: ProfileModel) {
    return state.totalCpdHoursRes;
  }

  @Selector()
  public static totalShortFallRes(state: ProfileModel) {
    return state.totalShortFallRes;
  }
  @Selector()
  public static profileDetails(state: ProfileModel) {
    return state.profile;
  }

  @Selector()
  public static eLearningLOV(state: ProfileModel) {
    return state.eLearninglOV;
  }

  @Selector()
  public static updateContactDetailsState(state: ProfileModel) {
    return state.updateContactDetails;
  }

  @Selector()
  public static updateSecretQuestionState(state: ProfileModel) {
    return state.updateSecretQuestionState;
  }

  @Selector()
  public static getchangePasswordState(state: ProfileModel) {
    return state.changePasswordState;
  }
  @Selector()
  public static downloadURL(state: ProfileModel) {
    return state.downloadCpdPdf;
  }

  @Action(SEARCH_ELEARNING)
  public searchELearning(
    { patchState }: StateContext<ProfileModel>,
    { payload }: SEARCH_ELEARNING,
  ) {
    return this.profileService.searchELearning(payload).pipe(
      takeUntilDestroyed(this.destroyRef),
      map((res) => {
        if (res.error) {
          patchState({
            searchedELearning: null,
            totalCpdRequiredRes: res.totalCpdRequired,
            totalCpdHoursRes: res.totalCpdHours,
            totalShortFallRes: res.totalShortFall,
          });
        } else {
          patchState({
            searchedELearning: res,
            totalCpdRequiredRes: res.totalCpdRequired,
            totalCpdHoursRes: res.totalCpdHours,
            totalShortFallRes: res.totalShortFall,
          });

          if (res.cpdTrainingList && res.cpdTrainingList.length > 0) {
            const data = {
              branchCode: payload.branchCode,
              agentCode: payload.agentCode,
              trainingYear: payload.year,
              shortfall: 0,
              isAdmin: payload.isAdmin,
            };
            this.profileService
              .downloadProfileCpdTraining(data)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe((res) => {
                patchState({
                  cpdDownloadUrl: res,
                });
              });
          }
        }
      }),
    );
  }
  @Action(GET_CPD_STATUS)
  public getCPDStatus(
    { patchState }: StateContext<ProfileModel>,
    { payload }: GET_CPD_STATUS,
  ) {
    return this.profileService.getELearningLOV(payload).pipe(
      map((res) => {
        const showCPD = Boolean(res.showCPD || res.showCDP);

        if (showCPD) {
          patchState({ showCPD });
          return showCPD;
        } else {
          patchState({ showCPD: false });
          return false;
        }
      }),
    );
  }

  @Action(GET_ELEARNING_LOV)
  public getElearningLOV(
    { patchState }: StateContext<ProfileModel>,
    { payload }: GET_ELEARNING_LOV,
  ) {
    return this.profileService.getELearningLOV(payload).pipe(
      map((res) => {
        const normalizedAgentList = this.normalizeAgentList(res?.agentList);
        const data = res
          ? {
              ...res,
              showCPD: Boolean(res.showCPD || res.showCDP),
              agentList: normalizedAgentList,
            }
          : null;
        patchState({
          eLearninglOV: data,
        });
      }),
    );
  }

  @Action(RESET_SEARCH_ELEARNING_DATA)
  public resetSearchELearningData({ patchState }: StateContext<ProfileModel>) {
    patchState({
      searchedELearning: null,
    });
  }

  @Action(GET_PROFILE_DETAILS)
  public getProfileDetails(
    { patchState }: StateContext<ProfileModel>,
    { payload }: GET_PROFILE_DETAILS,
  ) {
    return this.profileService.getProfileDetails(payload).pipe(
      map((res) => {
        if (res) {
          patchState({
            profile: res,
          });
        } else {
          patchState({
            profile: null,
          });
        }
      }),
    );
  }

  @Action(PROFILE_UPDATE_CONTACT_DETAILS)
  public updateContactDetails(
    { patchState }: StateContext<ProfileModel>,
    { payload }: PROFILE_UPDATE_CONTACT_DETAILS,
  ) {
    return this.profileService.updateProfileDetails(payload).pipe(
      map((res) => {
        if (res.success) {
          patchState({
            updateContactDetails: {
              isUpdateContactDetailsSuccess: true,
              updateContactDetailsErrorTitle: null,
              updateContactDetailsErrorMsg: null,
            },
          });
          this.store.dispatch(new UPDATE_PREFER_NAME(res));
        } else {
          patchState({
            updateContactDetails: {
              isUpdateContactDetailsSuccess: false,
              updateContactDetailsErrorTitle: res.error,
              updateContactDetailsErrorMsg: res.message,
            },
          });
        }
      }),
    );
  }
  @Action(PROFILE_CHANGE_SECRET_ANSWER)
  public changeSecretAnswer(
    { patchState }: StateContext<ProfileModel>,
    { payload }: PROFILE_CHANGE_SECRET_ANSWER,
  ) {
    return this.profileService.updateProfileDetails(payload).pipe(
      map((res) => {
        if (res.success) {
          patchState({
            updateSecretQuestionState: {
              isUpdateSecretQuestionSuccess: true,
              updateSecretQuestionErrorTitle: null,
              updateSecretQuestionErrorMsg: null,
            },
          });
        } else {
          patchState({
            updateSecretQuestionState: {
              isUpdateSecretQuestionSuccess: false,
              updateSecretQuestionErrorTitle: res.error,
              updateSecretQuestionErrorMsg: res.message,
            },
          });
        }
      }),
    );
  }
  @Action(RESET_PROFILE_STATE_RESPONSE)
  public resetprofileStateResponse({ patchState }: StateContext<ProfileModel>) {
    patchState({
      updateContactDetails: {
        isUpdateContactDetailsSuccess: false,
        updateContactDetailsErrorTitle: null,
        updateContactDetailsErrorMsg: null,
      },
      updateSecretQuestionState: {
        isUpdateSecretQuestionSuccess: false,
        updateSecretQuestionErrorTitle: null,
        updateSecretQuestionErrorMsg: null,
      },
    });
  }

  @Action(CHANGE_PASSWORD)
  public changePassword(
    { patchState }: StateContext<ProfileModel>,
    { payload }: CHANGE_PASSWORD,
  ) {
    return this.profileService.changePassword(payload).pipe(
      map((res) => {
        if (!res.success) {
          patchState({
            changePasswordState: {
              isChangePasswordSuccess: false,
              changePasswordErrorMsg: res.message,
              changePasswordErrorTitle: res.error,
            },
          });
        } else {
          this.store.dispatch(new SET_IS_PASSWORD_EXPIRED_IND(false));
          patchState({
            changePasswordState: {
              isChangePasswordSuccess: true,
              changePasswordErrorMsg: null,
              changePasswordErrorTitle: null,
            },
          });
        }
      }),
      catchError((err: Error) => {
        patchState({
          changePasswordState: {
            isChangePasswordSuccess: false,
            changePasswordErrorMsg: err?.message ?? null,
            changePasswordErrorTitle: err?.name ?? null,
          },
        });
        return EMPTY;
      }),
    );
  }

  @Action(GET_FILTERED_AGENT_LIST)
  public getFilteredAgentList(
    { patchState, getState }: StateContext<ProfileModel>,
    { payload }: GET_FILTERED_AGENT_LIST,
  ) {
    this.profileService
      .getFilteredAgentList(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        const state = getState();
        const normalizedAgentList = this.normalizeAgentList(res?.agentList);
        patchState({
          eLearninglOV: {
            ...state.eLearninglOV,
            agentList: normalizedAgentList,
          },
        });
      });
  }
}
